import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FiShoppingCart, FiSearch, FiMenu, FiX, FiChevronDown, FiUser, FiPackage, FiHeart, FiLogOut, FiGrid, FiShoppingBag } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import './Navbar.css';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropRef = useRef(null);

  // Scroll listener — adds 'scrolled' class after 80px
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close all menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/products?search=${encodeURIComponent(search.trim())}`);
      setSearch('');
      setMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setDropOpen(false);
    navigate('/');
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <header className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="navbar-inner container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img src="/fivora-logo.png" alt="Fivora" className="logo-img" />
          <span className="logo-text">Fivora</span>
        </Link>

        {/* Desktop Search */}
        <form className="navbar-search hide-mobile" onSubmit={handleSearch}>
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search products, vendors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>

        {/* Desktop Nav */}
        <nav className="navbar-links hide-mobile">
          <NavLink to="/products" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            Shop
          </NavLink>
          <NavLink to="/vendors" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            Vendors
          </NavLink>
          {!user && (
            <NavLink to="/vendor/apply" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
              Sell
            </NavLink>
          )}
        </nav>

        {/* Actions */}
        <div className="navbar-actions">
          {/* Cart */}
          <Link to="/cart" className="cart-btn" aria-label="Cart">
            <FiShoppingCart size={20} />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span
                  key="badge"
                  className="cart-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                >
                  {cartCount > 99 ? '99+' : cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>

          {user ? (
            /* User Dropdown */
            <div className="user-dropdown" ref={dropRef}>
              <button
                className="user-trigger"
                onClick={() => setDropOpen((o) => !o)}
                aria-expanded={dropOpen}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="avatar avatar-sm" />
                ) : (
                  <span className="avatar avatar-sm">{initials}</span>
                )}
                <span className="user-name hide-mobile">{user.name.split(' ')[0]}</span>
                <FiChevronDown
                  size={14}
                  style={{ transition: 'transform 0.2s', transform: dropOpen ? 'rotate(180deg)' : 'none' }}
                />
              </button>

              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: -8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="dropdown-header">
                      <p className="dropdown-name">{user.name}</p>
                      <p className="dropdown-email">{user.email}</p>
                    </div>
                    <div className="dropdown-divider" />

                    <Link to="/profile" className="dropdown-item" onClick={() => setDropOpen(false)}>
                      <FiUser size={15} /> Profile
                    </Link>
                    <Link to="/orders" className="dropdown-item" onClick={() => setDropOpen(false)}>
                      <FiPackage size={15} /> My Orders
                    </Link>
                    <Link to="/wishlist" className="dropdown-item" onClick={() => setDropOpen(false)}>
                      <FiHeart size={15} /> Wishlist
                    </Link>

                    {user.role === 'vendor' && (
                      <>
                        <div className="dropdown-divider" />
                        <Link to="/vendor/dashboard" className="dropdown-item vendor-link" onClick={() => setDropOpen(false)}>
                          <FiShoppingBag size={15} /> Vendor Panel
                        </Link>
                      </>
                    )}

                    {user.role === 'admin' && (
                      <>
                        <div className="dropdown-divider" />
                        <Link to="/admin/dashboard" className="dropdown-item vendor-link" onClick={() => setDropOpen(false)}>
                          <FiGrid size={15} /> Admin Panel
                        </Link>
                      </>
                    )}

                    <div className="dropdown-divider" />
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <FiLogOut size={15} /> Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="auth-links hide-mobile">
              <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
            </div>
          )}

          {/* Hamburger */}
          <button
            className="hamburger hide-desktop"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            {/* Search at top of mobile menu */}
            <form className="mobile-menu-search" onSubmit={handleSearch}>
              <FiSearch size={16} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search products, vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>

            <NavLink to="/products" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
              Shop
            </NavLink>
            <NavLink to="/vendors" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
              Vendors
            </NavLink>

            {!user ? (
              <>
                <NavLink to="/vendor/apply" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                  Sell on Fivora
                </NavLink>
                <div className="mobile-auth">
                  <Link to="/login" className="btn btn-ghost btn-block" onClick={() => setMenuOpen(false)}>Login</Link>
                  <Link to="/register" className="btn btn-primary btn-block" onClick={() => setMenuOpen(false)}>Sign Up</Link>
                </div>
              </>
            ) : (
              <>
                <NavLink to="/profile" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                  Profile
                </NavLink>
                <NavLink to="/orders" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                  My Orders
                </NavLink>
                <NavLink to="/wishlist" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                  Wishlist
                </NavLink>
                {user.role === 'vendor' && (
                  <NavLink to="/vendor/dashboard" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                    Vendor Panel
                  </NavLink>
                )}
                {user.role === 'admin' && (
                  <NavLink to="/admin/dashboard" className={({ isActive }) => `mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>
                    Admin Panel
                  </NavLink>
                )}
                <button className="mobile-link mobile-logout" onClick={handleLogout}>Logout</button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
